import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import PricingSection from './subscription/PricingSection';
import {fetchPaddleProducts, fetchSubscriptions} from '../store/slices/subscriptionThunks';
import {
  currentPlanSelector,
  hasPausedSubscriptionSelector,
  hasSubscriptionSelector,
  productsApiSelector,
  productsSelector,
  subscriptionsApiSelector,
} from '../store/selectors/subscription.selectors';
import {isAuthenticatedSelector} from '../store/selectors/auth.selectors';
import {TRIAL_DAYS, TRIAL_PRICE_USD} from '../domain/plans';
import type {BillingCycleName} from '../domain/types';

/**
 * The single owner of pricing state. The marketing site fetched `/paddle/products` and
 * `/paddle/subscriptions` in three separate components with three copies of the plan-derivation
 * logic; that now lives in the subscription slice + selectors and is read from here only.
 *
 * Rendered on both the public landing page and the signed-in account page.
 */
const PricingContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const products = useAppSelector(productsSelector);
  const productsApi = useAppSelector(productsApiSelector);
  const subscriptionsApi = useAppSelector(subscriptionsApiSelector);
  const currentPlan = useAppSelector(currentPlanSelector);
  const hasSubscription = useAppSelector(hasSubscriptionSelector);
  const hasPausedSubscription = useAppSelector(hasPausedSubscriptionSelector);

  // Pricing is public and effectively static, so it is fetched once per page load.
  useEffect(() => {
    if (productsApi.status === 'idle') {
      dispatch(fetchPaddleProducts());
    }
  }, [dispatch, productsApi.status]);

  // Subscriptions are re-read whenever the signed-in user changes.
  useEffect(() => {
    if (isAuthenticated && subscriptionsApi.status === 'idle') {
      dispatch(fetchSubscriptions());
    }
  }, [dispatch, isAuthenticated, subscriptionsApi.status]);

  /**
   * Signed out, this is signup: the account does not exist yet, so it cannot go to `/checkout` (which
   * needs a customer id for a user we do not have). `/signup` runs the trial checkout instead, and
   * carries the cycle so the visitor lands on the card they clicked.
   */
  const handleSubscribe = (priceId: string, billingCycle: BillingCycleName) => {
    if (!isAuthenticated) {
      navigate(`/signup?plan=${billingCycle}`);
      return;
    }
    navigate(`/checkout?priceId=${encodeURIComponent(priceId)}`);
  };

  const heading = hasSubscription
    ? 'Your Subscription'
    : isAuthenticated
      ? 'Subscribe to Pro'
      : 'Simple, transparent pricing';

  const subheading = hasSubscription
    ? 'You\'re on Pro. Manage billing from your account settings.'
    : `Start with a ${TRIAL_DAYS}-day trial for $${TRIAL_PRICE_USD} — 25 hours of transcription a `
      + 'month and files up to 4 hours long. Cancel any time.';

  return (
    <PricingSection
      heading={heading}
      subheading={subheading}
      products={products}
      status={productsApi.status}
      error={productsApi.error}
      isAuthenticated={isAuthenticated}
      currentPlan={currentPlan}
      hasSubscription={hasSubscription}
      hasPausedSubscription={hasPausedSubscription}
      onSubscribe={handleSubscribe}
    />
  );
};

export default PricingContainer;
